import { BsPencilSquare } from '@icon/react-icons';
import { createControlField, useBoolean, useSnackbarInstance } from '@lib/common';
import React, { useCallback, useMemo } from 'react';
import { useState, useEffect } from 'react';
import { ImageEditor } from '@components/image-editor-dialog';
import { OverlayContainer, Overlay } from '@components/overlay';
import PublishIcon from '@material-ui/icons/Publish';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { IconButton, Grid, Tooltip } from '@material-ui/core';
import { windowObj } from '@lib/common';
import { usePersonFormContext } from './person.form.context';
import { useDefaultImage } from '@lib/common';

export interface EditableAvatarProps
  extends Omit<
    React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
    'src' | 'srcList' | 'onChange'
  > {
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
  value?: string;
  empty?: React.ReactNode;
  hideDelete?: boolean;
  hideEdit?: boolean;
  hideUpload?: boolean;
  hideDownload?: boolean;
  onChange?: (value: string) => any;
  onDelete?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

export const PosterField = ({
  height = 128,
  width = 128,
  value: src = null,
  empty = 'None',
  onChange = null,
  onDelete = null,
  hideDelete = false,
  hideEdit = false,
  hideUpload = false,
  hideDownload = false,
  ...props
}: EditableAvatarProps) => {
  const { form, editMode } = usePersonFormContext();

  const [showEditor, openEditor, closeEditor] = useBoolean(false);
  const [image, setImage] = useState<File | string>(null);
  const [value, setValue] = useState(src);
  const { enqueue } = useSnackbarInstance();
  const srcList = useMemo(() => {
    if (editMode) {
      return [
        `${windowObj.RAZZLE_PERSON_LOGO_PROFILE}/${form.vtr_id}.png?ts=${encodeURIComponent(form.modified_at)}`,
        `${windowObj.RAZZLE_TMDB_IMAGE_URL}${form.profile_path}`,
        '/images/content_default.svg',
      ].filter(e => e);
    }
    return null;
  }, [form, editMode]);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: null,
    useSuspense: false,
  });
  useEffect(() => setValue(src || fallbackedSource), [src, fallbackedSource]);

  const handleFileChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const file = evt.target.files[0];
      if (!file) return;
      if (file.size > 5000000) {
        enqueue('Image size cannot exceed 5000kb', { variant: 'error' });
        return;
      }
      setImage(file);
      evt.target.value = null; //triggers changes even after u choose the same file
      openEditor();
    },
    [openEditor, setImage, enqueue]
  );
  const onEditClick = () => {
    const source = value == 'deleted' ? null : (value && value.trim()) || fallbackedSource;
    setImage(source);
    openEditor();
  };

  const onDeleteClick: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = evt => {
    setImage(null);
    editMode ? setValue('deleted') : setValue(null);
    editMode ? onChange && onChange('deleted') : onChange && onChange(null);
    onDelete && onDelete(evt);
  };

  const onEditorDone = useCallback(
    ({ imageCanvas }) => {
      const dataUrl = imageCanvas.toDataURL();
      setValue(dataUrl);
      onChange && onChange(dataUrl);
    },
    [setValue, onChange]
  );

  const hasValue = !!((value && value !== 'deleted') || null);

  return (
    <>
      <OverlayContainer>
        {!hasValue ? (
          <div
            style={{
              height,
              width,
              background: '#aaa',
              display: 'flex',
              justifyContent: 'center',
              color: 'white',
              alignItems: 'center',
            }}>
            {empty}
          </div>
        ) : (
          <img
            src={value || null}
            style={{ ...props?.style, height, width, ...containImageStyle.style }}
            {...props}
          />
        )}
        <Overlay $center={true}>
          <div
            style={{
              display: 'flex',
              background: 'rgba(21,21,21,.6)',
              justifyContent: 'center',
              alignItems: 'center',
              height,
              width,
              userSelect: 'none',
            }}>
            <Grid
              container
              spacing={1}
              justify="center"
              alignItems="center"
              style={{ width: (width as number) / 2 }}>
              {hasValue && !hideEdit && (
                <Grid item xs={6}>
                  <Tooltip title="Edit the image">
                    <IconButton style={{ color: 'white' }} onClick={onEditClick}>
                      <BsPencilSquare />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              {hasValue && !hideDelete && (
                <Grid item xs={6}>
                  <Tooltip title="Delete the image">
                    <IconButton style={{ color: 'white' }} onClick={onDeleteClick}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              {!hideUpload && (
                <Grid item xs={6}>
                  <Tooltip title="Upload new image">
                    <span>
                      <IconButton style={{ color: 'white' }}>
                        <input
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="contained-button-file"
                          type="file"
                        />
                        <label htmlFor="contained-button-file" style={{ cursor: 'pointer' }}>
                          <PublishIcon />
                        </label>
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              )}
              {hasValue && !hideDelete && (
                <Grid item xs={6}>
                  <Tooltip title="Download the image">
                    <IconButton style={{ color: 'white' }} component="a" href={value} download>
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </div>
        </Overlay>
      </OverlayContainer>
      {showEditor && image && (
        <ImageEditor
          image={image}
          open={showEditor}
          onClose={closeEditor}
          onFinish={onEditorDone}
          width={150}
          height={200}
        />
      )}
    </>
  );
};

const containImageStyle = { style: { objectFit: 'contain' } } as const;

export const PersonPosterField = createControlField(props => (
  <PosterField {...props} width={150} height={200} />
));
